import { useState, useEffect, useCallback } from 'react';
import { establishmentAPI, employeeAPI } from '../api/apiService';

/**
 * Custom hook to manage dropdown data for establishments, employees, and departments
 */
export const useDropdownData = () => {
  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState({
    establishments: false,
    employees: false,
    departments: false
  });
  const [errors, setErrors] = useState({
    establishments: null,
    employees: null,
    departments: null
  });

  // Fetch all establishments
  const fetchEstablishments = useCallback(async () => {
    setLoading(prev => ({ ...prev, establishments: true }));
    setErrors(prev => ({ ...prev, establishments: null }));
    try {
      const response = await establishmentAPI.getAll();
      setEstablishments(response.establishments || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setErrors(prev => ({ ...prev, establishments: error.message }));
      setEstablishments([]);
    } finally {
      setLoading(prev => ({ ...prev, establishments: false }));
    }
  }, []);

  // Fetch employees by establishment
  const fetchEmployeesByEstablishment = useCallback(async (establishmentId) => {
    if (!establishmentId) {
      setEmployees([]);
      return;
    }

    setLoading(prev => ({ ...prev, employees: true }));
    setErrors(prev => ({ ...prev, employees: null }));
    try {
      const response = await employeeAPI.getByEstablishment(establishmentId);
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setErrors(prev => ({ ...prev, employees: error.message }));
      setEmployees([]);
    } finally {
      setLoading(prev => ({ ...prev, employees: false }));
    }
  }, []);

  // Fetch all employees
  const fetchAllEmployees = useCallback(async () => {
    setLoading(prev => ({ ...prev, employees: true }));
    setErrors(prev => ({ ...prev, employees: null }));
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setErrors(prev => ({ ...prev, employees: error.message }));
      setEmployees([]);
    } finally {
      setLoading(prev => ({ ...prev, employees: false }));
    }
  }, []);

  // Fetch departments by establishment
  const fetchDepartmentsByEstablishment = useCallback(async (establishmentId) => {
    setLoading(prev => ({ ...prev, departments: true }));
    setErrors(prev => ({ ...prev, departments: null }));
    try {
      const response = await establishmentAPI.getDepartments(establishmentId);
      setDepartments(response.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setErrors(prev => ({ ...prev, departments: error.message }));

      // Fallback to default departments if endpoint not available (405 error)
      // This allows the form to work while backend deployment is pending
      setDepartments([
        'Engineering',
        'Marketing',
        'Sales',
        'HR',
        'Finance',
        'Operations',
        'IT',
        'Administration'
      ]);
    } finally {
      setLoading(prev => ({ ...prev, departments: false }));
    }
  }, []);

  // Fetch all departments
  const fetchAllDepartments = useCallback(async () => {
    setLoading(prev => ({ ...prev, departments: true }));
    setErrors(prev => ({ ...prev, departments: null }));
    try {
      const response = await establishmentAPI.getDepartments();
      setDepartments(response.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setErrors(prev => ({ ...prev, departments: error.message }));

      // Fallback to default departments if endpoint not available
      setDepartments([
        'Engineering',
        'Marketing',
        'Sales',
        'HR',
        'Finance',
        'Operations',
        'IT',
        'Administration'
      ]);
    } finally {
      setLoading(prev => ({ ...prev, departments: false }));
    }
  }, []);

  return {
    // Data
    establishments,
    employees,
    departments,

    // Loading states
    loading,

    // Error states
    errors,

    // Fetch functions
    fetchEstablishments,
    fetchEmployeesByEstablishment,
    fetchAllEmployees,
    fetchDepartmentsByEstablishment,
    fetchAllDepartments,

    // Utility functions
    clearEmployees: () => setEmployees([]),
    clearDepartments: () => setDepartments([]),
  };
};

export default useDropdownData;
